import { Check } from "@mui/icons-material";
import { keyframes, styled } from "@mui/material/styles";

const bounceInAnimation = keyframes`
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(10deg); }
  70% { transform: scale(0.9) rotate(-5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
`;

const AnimatedIcon = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${bounceInAnimation} 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards`,
});

const CelebrationContainer = styled("div")({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const CheckCircle = styled("div", {
  shouldForwardProp: (prop) => prop !== "$animate",
})<{ $animate?: boolean }>(({ $animate }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "2px solid #22c55e",
  background: "transparent",
  ...($animate && {
    animation: `${pulseGlow} 1s ease-out`,
  }),
}));

const createSparkleAnimation = (tx: number, ty: number) => keyframes`
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    transform: translate(${tx}px, ${ty}px) scale(1.2);
    opacity: 0;
  }
`;

const SparkleBase = styled("div", {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>(({ $color }) => ({
  position: "absolute",
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: $color,
}));

const Sparkle1 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-20, -16)} 0.5s ease-out forwards`,
});

const Sparkle2 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(18, -20)} 0.5s ease-out 0.05s forwards`,
});

const Sparkle3 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(24, 8)} 0.5s ease-out 0.1s forwards`,
});

const Sparkle4 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-24, 12)} 0.5s ease-out 0.15s forwards`,
});

const Sparkle5 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(8, 22)} 0.5s ease-out 0.08s forwards`,
});

const Sparkle6 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-14, 20)} 0.5s ease-out 0.12s forwards`,
});

export function SuccessSparkle({
  sparkles = true,
  pulse = true,
}: {
  sparkles?: boolean;
  pulse?: boolean;
}) {
  return (
    <CelebrationContainer>
      {sparkles && (
        <>
          <Sparkle1 $color="#22C55E" />
          <Sparkle2 $color="#86EFAC" />
          <Sparkle3 $color="#4ADE80" />
          <Sparkle4 $color="#BBF7D0" />
          <Sparkle5 $color="#22C55E" />
          <Sparkle6 $color="#86EFAC" />
        </>
      )}
      <AnimatedIcon>
        <CheckCircle $animate={pulse}>
          <Check sx={{ color: "#22C55E", fontSize: 18 }} />
        </CheckCircle>
      </AnimatedIcon>
    </CelebrationContainer>
  );
}

export function StaticSuccessCheck() {
  return (
    <CheckCircle>
      <Check sx={{ color: "#22C55E", fontSize: 18 }} />
    </CheckCircle>
  );
}
